/**
 * User Management Hooks
 * TanStack Query hooks for user operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as usersService from '@/services/users.service';
import type { User } from '@/types/api.types';
import type { UserListParams, UserListResponse } from '@/services/users.service';

/**
 * Query hook to list all users (authenticated)
 */
export const useUsers = (params?: UserListParams) => {
  return useQuery({
    queryKey: ['users', params],
    queryFn: () => usersService.listUsers(params),
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Query hook to get a single user
 */
export const useUser = (userId: string | null) => {
  return useQuery({
    queryKey: ['users', userId],
    queryFn: () => usersService.getUserById(userId!),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Admin: Query hook to list all users with filtering
 */
export const useUsersAdmin = (params?: UserListParams) => {
  return useQuery({
    queryKey: ['users-admin', params],
    queryFn: () => usersService.listUsersAdmin(params),
    staleTime: 10 * 1000, // 10 seconds (more frequent updates for admin)
  });
};

/**
 * Admin: Mutation hook to approve a pending user
 */
export const useApproveUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => usersService.approveUser(userId),
    onSuccess: (updatedUser) => {
      // Update user in admin list
      queryClient.setQueryData<UserListResponse>(
        ['users-admin'],
        (old) => {
          if (!old?.users) return old;
          return {
            ...old,
            users: old.users.map((user: User) =>
              user.id === updatedUser.id ? updatedUser : user
            ),
          };
        }
      );

      // Invalidate to refetch with updated status
      queryClient.invalidateQueries({ queryKey: ['users-admin'] });
      queryClient.setQueryData(['users', updatedUser.id], updatedUser);
    },
  });
};

/**
 * Admin: Mutation hook to change user role
 */
export const useChangeUserRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      role,
    }: {
      userId: string;
      role: 'admin' | 'employee';
    }) => usersService.changeUserRole(userId, role),
    onSuccess: (updatedUser) => {
      // Update user in admin list
      queryClient.setQueryData<UserListResponse>(
        ['users-admin'],
        (old) => {
          if (!old?.users) return old;
          return {
            ...old,
            users: old.users.map((user: User) =>
              user.id === updatedUser.id ? updatedUser : user
            ),
          };
        }
      );

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['users-admin'] });
      queryClient.setQueryData(['users', updatedUser.id], updatedUser);
    },
  });
};

/**
 * Admin: Mutation hook to deactivate user
 */
export const useDeactivateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => usersService.deactivateUser(userId),
    onSuccess: (updatedUser) => {
      // Update user in admin list
      queryClient.setQueryData<UserListResponse>(
        ['users-admin'],
        (old) => {
          if (!old?.users) return old;
          return {
            ...old,
            users: old.users.map((user: User) =>
              user.id === updatedUser.id ? updatedUser : user
            ),
          };
        }
      );

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['users-admin'] });
      queryClient.setQueryData(['users', updatedUser.id], updatedUser);
    },
  });
};

/**
 * Admin: Mutation hook to reactivate user
 */
export const useReactivateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => usersService.reactivateUser(userId),
    onSuccess: (updatedUser) => {
      // Update user in admin list
      queryClient.setQueryData<UserListResponse>(
        ['users-admin'],
        (old) => {
          if (!old?.users) return old;
          return {
            ...old,
            users: old.users.map((user: User) =>
              user.id === updatedUser.id ? updatedUser : user
            ),
          };
        }
      );

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['users-admin'] });
      queryClient.setQueryData(['users', updatedUser.id], updatedUser);
    },
  });
};

/**
 * Admin: Mutation hook to permanently delete user
 */
export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => usersService.deleteUser(userId),
    onSuccess: (result) => {
      // Remove user from admin list
      queryClient.setQueryData<UserListResponse>(
        ['users-admin'],
        (old) => {
          if (!old?.users) return old;
          return {
            ...old,
            users: old.users.filter((user: User) => user.id !== result.id),
            pagination: {
              ...old.pagination,
              total: Math.max(0, old.pagination.total - 1),
            },
          };
        }
      );

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['users-admin'] });
      queryClient.removeQueries({ queryKey: ['users', result.id] });
    },
  });
};
